"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DotnetConditionValidator = void 0;
/*---------------------------------------------------------------------------------------------
*  Licensed to the .NET Foundation under one or more agreements.
*  The .NET Foundation licenses this file to you under the MIT license.
*--------------------------------------------------------------------------------------------*/
const EventStreamEvents_1 = require("../EventStream/EventStreamEvents");
const CommandExecutor_1 = require("../Utils/CommandExecutor");
const FileUtilities_1 = require("../Utils/FileUtilities");
const CacheTimeConstants_1 = require("./CacheTimeConstants");
const versionUtils = require("./VersionUtilities");
class DotnetConditionValidator {
    constructor(workerContext, utilityContext, executor) {
        var _a;
        this.workerContext = workerContext;
        this.utilityContext = utilityContext;
        this.executor = executor;
        (_a = this.executor) !== null && _a !== void 0 ? _a : (this.executor = new CommandExecutor_1.CommandExecutor(this.workerContext, this.utilityContext));
    }
    dotnetMeetsRequirement(dotnetExecutablePath, requirement) {
        return __awaiter(this, void 0, void 0, function* () {
            const hostArch = yield this.getHostArchitecture(dotnetExecutablePath, requirement);
            if (requirement.acquireContext.mode === 'sdk') {
                const availableSDKs = yield this.getSDKs(dotnetExecutablePath);
                if (availableSDKs.some((sdk) => {
                    return this.stringArchitectureMeetsRequirement(hostArch, requirement.acquireContext.architecture) &&
                        this.stringVersionMeetsRequirement(sdk.version, requirement.acquireContext.version, requirement) && this.allowPreview(sdk.version, requirement);
                })) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetConditionsValidated(`${dotnetExecutablePath} satisfies the conditions.`));
                    return true;
                }
            }
            else {
                // No need to consider SDKs when looking for runtimes as all the runtimes installed with the SDKs will be included in the runtimes list.
                const availableRuntimes = yield this.getRuntimes(dotnetExecutablePath);
                if (availableRuntimes.some((runtime) => {
                    return runtime.mode === requirement.acquireContext.mode && this.stringArchitectureMeetsRequirement(hostArch, requirement.acquireContext.architecture) &&
                        this.stringVersionMeetsRequirement(runtime.version, requirement.acquireContext.version, requirement) && this.allowPreview(runtime.version, requirement);
                })) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetConditionsValidated(`${dotnetExecutablePath} satisfies the conditions.`));
                    return true;
                }
            }
            this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetFindPathDidNotMeetCondition(`${dotnetExecutablePath} did NOT satisfy the conditions: hostArch: ${hostArch}, requiredArch: ${requirement.acquireContext.architecture},
            required version: ${requirement.acquireContext.version}, required mode: ${requirement.acquireContext.mode}`));
            return false;
        });
    }
    /**
     *
     * @param hostPath The path to the dotnet executable
     * @returns The architecture of the dotnet host from the PATH, in dotnet info string format
     * The .NET Host will only list versions of the runtime and sdk that match its architecture.
     * Thus, any runtime or sdk that it prints out will be the same architecture as the host.
     * This information is not always accurate as dotnet info is subject to change.
     *
     * @remarks Will return '' if the architecture cannot be determined for some peculiar reason (e.g. dotnet --info is broken or changed).
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    getHostArchitecture(hostPath, requirement) {
        return __awaiter(this, void 0, void 0, function* () {
            // dotnet --info is not machine-readable and subject to breaking changes. See https://github.com/dotnet/sdk/issues/33697 and https://github.com/dotnet/runtime/issues/98735/
            // Unfortunately even with a new API, that might not go in until .NET 10 and beyond, so we have to rely on dotnet --info for now.*/
            if (!hostPath || hostPath === '""') {
                return '';
            }
            const infoCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${hostPath}"`, ['--info']);
            const envWithForceEnglish = process.env;
            envWithForceEnglish.DOTNET_CLI_UI_LANGUAGE = 'en-US';
            // System may not have english installed, but CDK already calls this without issue -- the .NET SDK language invocation is also wrapped by a runtime library and natively includes english assets
            const hostArch = yield (this.executor).execute(infoCommand, { env: envWithForceEnglish, dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                const lines = result.stdout.split('\n').map((line) => line.trim()).filter((line) => { var _a; return ((_a = line === null || line === void 0 ? void 0 : line.length) !== null && _a !== void 0 ? _a : 0) > 0; });
                // This is subject to change but there is no good alternative to do this
                const archLine = lines.find((line) => line.startsWith('Architecture:'));
                if (archLine === undefined) {
                    this.workerContext.eventStream.post(new EventStreamEvents_1.DotnetUnableToCheckPATHArchitecture(`Could not find the architecture of the dotnet host ${hostPath}. If this host does not match the architecture ${requirement.acquireContext.architecture}:
Please set the PATH to a dotnet host that matches the architecture ${requirement.acquireContext.architecture}. An incorrect architecture will cause instability for the extension ${requirement.acquireContext.requestingExtensionId}.`));
                    if (process.env.DOTNET_INSTALL_TOOL_DONT_ACCEPT_UNKNOWN_ARCH === '1') {
                        return 'unknown'; // Bad value to cause failure mismatch, which will become 'auto'
                    }
                    else {
                        return '';
                    }
                }
                const arch = archLine.split(' ')[1];
                return arch;
            });
            return hostArch;
        });
    }
    getSDKs(existingPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!existingPath || existingPath === '""') {
                return [];
            }
            const findSDKsCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${existingPath}"`, ['--list-sdks']);
            const sdkInfo = yield (this.executor).execute(findSDKsCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                if (result.status !== '0') {
                    return [];
                }
                const sdks = result.stdout.split('\n').map((line) => line.trim()).filter((line) => { var _a; return ((_a = line === null || line === void 0 ? void 0 : line.length) !== null && _a !== void 0 ? _a : 0) > 0; });
                const sdkInfos = sdks.map((sdk) => {
                    const parts = sdk.split(' ', 2);
                    return {
                        mode: 'sdk',
                        version: parts[0],
                        directory: sdk.split(' ').slice(1).join(' ').slice(1, -1) // need to remove the brackets from the path [path]
                    };
                }).filter(x => x !== null);
                return sdkInfos;
            });
            return sdkInfo;
        });
    }
    stringVersionMeetsRequirement(availableVersion, requestedVersion, requirement) {
        var _a;
        const availableMajor = Number(versionUtils.getMajor(availableVersion, this.workerContext.eventStream, this.workerContext));
        const requestedMajor = Number(versionUtils.getMajor(requestedVersion, this.workerContext.eventStream, this.workerContext));
        const requestedPatchStr = requirement.acquireContext.mode !== 'sdk' ? versionUtils.getRuntimePatchVersionString(requestedVersion, this.workerContext.eventStream, this.workerContext)
            : versionUtils.getSDKCompleteBandAndPatchVersionString(requestedVersion, this.workerContext.eventStream, this.workerContext);
        const requestedPatch = requestedPatchStr ? Number(requestedPatchStr) : null;
        const adjustedVersionSpec = [requirement.versionSpecRequirement].map(x => {
            switch (x) {
                case 'latestMajor':
                    return 'greater_than_or_equal';
                case 'disable':
                    return 'equal';
                default:
                    return x;
            }
        }).at(0);
        if (availableMajor === requestedMajor) {
            const availableMinor = Number(versionUtils.getMinor(availableVersion, this.workerContext.eventStream, this.workerContext));
            const requestedMinor = Number(versionUtils.getMinor(requestedVersion, this.workerContext.eventStream, this.workerContext));
            if (availableMinor === requestedMinor && requestedPatch) {
                const availablePatchStr = requirement.acquireContext.mode !== 'sdk' ?
                    versionUtils.getRuntimePatchVersionString(availableVersion, this.workerContext.eventStream, this.workerContext)
                    :
                        (() => {
                            const band = versionUtils.getSDKCompleteBandAndPatchVersionString(availableVersion, this.workerContext.eventStream, this.workerContext);
                            if (band) {
                                return band;
                            }
                            return null;
                        })();
                const availablePatch = availablePatchStr ? Number(availablePatchStr) : null;
                const availableBandStr = requirement.acquireContext.mode === 'sdk' ?
                    (() => {
                        const featureBand = versionUtils.getFeatureBandFromVersion(availableVersion, this.workerContext.eventStream, this.workerContext, false);
                        if (featureBand) {
                            return featureBand;
                        }
                        return null;
                    })() : null;
                const availableBand = availableBandStr ? Number(availableBandStr) : null;
                switch (adjustedVersionSpec) {
                    // the 'availablePatch' must exist, since the version is from --list-runtimes or --list-sdks.
                    case 'equal':
                        return availablePatch === requestedPatch;
                    case 'greater_than_or_equal':
                    case 'latestFeature':
                        return availablePatch >= requestedPatch;
                    case 'less_than_or_equal':
                        return availablePatch <= requestedPatch;
                    case 'latestPatch':
                        const requestedBandStr = requirement.acquireContext.mode === 'sdk' ? (_a = versionUtils.getFeatureBandFromVersion(requestedVersion, this.workerContext.eventStream, this.workerContext, false)) !== null && _a !== void 0 ? _a : null : null;
                        const requestedBand = requestedBandStr ? Number(requestedBandStr) : null;
                        return availablePatch >= requestedPatch && (availableBand ? availableBand === requestedBand : true);
                }
            }
            else {
                switch (adjustedVersionSpec) {
                    case 'equal':
                        return availableMinor === requestedMinor;
                    case 'greater_than_or_equal':
                        return availableMinor >= requestedMinor;
                    case 'less_than_or_equal':
                        return availableMinor <= requestedMinor;
                    case 'latestPatch':
                    case 'latestFeature':
                        return false;
                }
            }
        }
        else {
            switch (adjustedVersionSpec) {
                case 'equal':
                    return false;
                case 'greater_than_or_equal':
                    return availableMajor >= requestedMajor;
                case 'less_than_or_equal':
                    return availableMajor <= requestedMajor;
                case 'latestPatch':
                case 'latestFeature':
                    return false;
            }
        }
    }
    stringArchitectureMeetsRequirement(outputArchitecture, requiredArchitecture) {
        return !requiredArchitecture || !outputArchitecture || FileUtilities_1.FileUtilities.dotnetInfoArchToNodeArch(outputArchitecture, this.workerContext.eventStream) === requiredArchitecture;
    }
    allowPreview(availableVersion, requirement) {
        if (requirement.rejectPreviews === true) {
            return !versionUtils.isPreviewVersion(availableVersion, this.workerContext.eventStream, this.workerContext);
        }
        return true;
    }
    getRuntimes(existingPath) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!existingPath || existingPath === '""') {
                return [];
            }
            const findRuntimesCommand = CommandExecutor_1.CommandExecutor.makeCommand(`"${existingPath}"`, ['--list-runtimes']);
            const windowsDesktopString = 'Microsoft.WindowsDesktop.App';
            const aspnetCoreString = 'Microsoft.AspNetCore.App';
            const runtimeString = 'Microsoft.NETCore.App';
            const runtimeInfo = yield (this.executor).execute(findRuntimesCommand, { dotnetInstallToolCacheTtlMs: CacheTimeConstants_1.DOTNET_INFORMATION_CACHE_DURATION_MS }, false).then((result) => {
                if (result.status !== '0') {
                    return [];
                }
                const runtimes = result.stdout.split('\n').map((line) => line.trim()).filter((line) => { var _a; return ((_a = line === null || line === void 0 ? void 0 : line.length) !== null && _a !== void 0 ? _a : 0) > 0; });
                const runtimeInfos = runtimes.map((runtime) => {
                    const parts = runtime.split(' ', 3); // account for spaces in PATH, no space should appear before then and luckily path is last
                    return {
                        mode: parts[0] === aspnetCoreString ? 'aspnetcore' : parts[0] === runtimeString ? 'runtime' : 'sdk', // sdk is a placeholder for windows desktop, will never match since this is for runtime search only
                        version: parts[1],
                        directory: runtime.split(' ').slice(2).join(' ').slice(1, -1) // account for spaces in PATH, no space should appear before then and luckily path is last.
                        // the 2nd slice needs to remove the brackets from the path [path]
                    };
                }).filter(x => x !== null);
                return runtimeInfos;
            });
            return runtimeInfo;
        });
    }
}
exports.DotnetConditionValidator = DotnetConditionValidator;
//# sourceMappingURL=DotnetConditionValidator.js.map